import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  createFileRoute,
  useNavigate,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";
import { parse } from "graphql";
import { useEffect, useMemo, useState, type JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import type { EstenderRecorrenciaInput } from "@repo/graphql-types/graphql";
import {
  CalendarPageAppointmentsQueryDocument,
  CalendarPageFreeSlotsQueryDocument,
  RecurringPatientLimitsQueryDocument,
  WaitingRoomPageQueryDocument,
} from "@repo/graphql-types/graphql";
import { CpsCheckboxV2 } from "corpus";

import { RecurrenceCreateHeader } from "@/components/recurring-appointment/recurrence-create-header";
import { RecurrenceReviewInfoCard } from "@/components/recurring-appointment/recurrence-review-info-card";
import { RecurrenceReviewPatientCard } from "@/components/recurring-appointment/recurrence-review-patient-card";
import { RecurrenceSessionsList } from "@/components/recurring-appointment/recurrence-sessions-list";
import { RecurrenceSessionsListSkeleton } from "@/components/recurring-appointment/recurrence-sessions-list-skeleton";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import type {
  RecurringSession,
  RecurringSessionResolution,
} from "@/lib/atoms/recurring-appointment-form-atom";
import { removeSecondsFromTimeString } from "@/lib/helpers/date";
import { formatPhoneNumber } from "@/lib/helpers/misc";
import {
  appendNextDate,
  buildEstenderRecorrenciaInput,
  buildRecurrenceSlots,
  canConfirmRecurrence,
  formatRecurrenceScheduleLabel,
  getExtendAnchorDate,
  getFrequencyLabel,
  getNextRecurrenceDate,
  getQuotaUsedSessionsCount,
  getUsedSessionsCount,
  keepSession,
  resolveSessionAsConsultorioVirtual,
  seedSessionsCountingAvailable,
  skipSession,
  takeDatesUntilAvailable,
} from "@/lib/helpers/recurrence";
import { COD_CONSULTORIO_VIRTUAL, isConsultorioVirtual } from "@/lib/helpers/unit";
import {
  mapNodeToRaw,
  RecurringAppointmentDetailPageQuery,
} from "@/lib/queries/recurrence-detail-query";
import { trackEvent } from "@/lib/tracking";

interface EstenderRecorrenciaMutationData {
  estenderAgendamentoRecorrente: {
    data: {
      codAgendamentoRecorrente: number;
      codAgendamentos: number[];
      datasNaoAgendadas: string[];
    } | null;
    errors: { __typename: string; message: string }[] | null;
  };
}

interface EstenderRecorrenciaMutationVariables {
  input: EstenderRecorrenciaInput;
}

const EstenderRecorrenciaMutation = parse(/* GraphQL */ `
  mutation EstenderRecorrencia($input: EstenderRecorrenciaInput!) {
    estenderAgendamentoRecorrente(input: { input: $input }) {
      data: agendamentoRecorrenteOutput {
        codAgendamentoRecorrente
        codAgendamentos
        datasNaoAgendadas
      }
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`) as TypedDocumentNode<
  EstenderRecorrenciaMutationData,
  EstenderRecorrenciaMutationVariables
>;

const RecurrenceExtendAvailabilityQuery = graphql(/* GraphQL */ `
  query RecurrenceExtendAvailabilityQuery(
    $codUnidade: Int!
    $codCompromisso: Int!
    $codFormaRecebimento: Int!
    $dataInicio: Date!
    $horarioInicio: TimeSpan!
    $frequencia: Int!
    $quantidadeSlots: Int!
  ) {
    buscaHorarioRecorrenteEspecifico(
      codUnidade: $codUnidade
      codCompromisso: $codCompromisso
      codFormaRecebimento: $codFormaRecebimento
      dataInicio: $dataInicio
      horarioInicio: $horarioInicio
      frequencia: $frequencia
      quantidadeSlots: $quantidadeSlots
    ) {
      blocos {
        horaInicio
        horaFim
        datas {
          data
          disponivel
          motivoIndisponibilidade
        }
      }
    }
  }
`);

const RecurringAppointmentExtendPage = (): JSX.Element => {
  const { recurringAppointmentId } = Route.useParams();
  const codRecorrencia = Number(recurringAppointmentId);
  const navigate = useNavigate();
  const router = useRouter();
  const { user } = useRouteContext({ from: "__root__" });

  const queryResult = useGraphQL(RecurringAppointmentDetailPageQuery, {
    codAgendamentoRecorrente: codRecorrencia,
  });
  const { data } = queryResult;

  const recurrence = data?.agendamentosRecorrentes?.[0] ?? null;
  const firstAppointment = data?.agendamentos?.[0] ?? null;

  const blockUnit = useMemo(() => {
    const codUnidade = recurrence?.codUnidade ?? firstAppointment?.codUnidade ?? 0;
    const naUnidade =
      data?.agendamentos?.find((appointment) => appointment.codUnidade === codUnidade) ??
      firstAppointment;

    return {
      codUnidade,
      nome: naUnidade?.Unidade.nome ?? "",
      sigla: naUnidade?.Unidade.sigla ?? "",
    };
  }, [recurrence?.codUnidade, data?.agendamentos, firstAppointment]);

  const slots = useMemo(() => {
    const roots = (data?.agendamentos ?? []).map(mapNodeToRaw);
    return buildRecurrenceSlots(roots, codRecorrencia).sort((a, b) =>
      a.data.localeCompare(b.data),
    );
  }, [data?.agendamentos, codRecorrencia]);

  const codPaciente = recurrence?.codPaciente ?? null;
  const limitsResult = useGraphQL(
    RecurringPatientLimitsQueryDocument,
    { codPacientes: codPaciente != null ? [codPaciente] : [] },
    { enabled: codPaciente != null, staleTime: 0, gcTime: 0 },
  );
  const patientLimit =
    limitsResult.data?.limitesRecorrenciaPorPaciente?.find(
      (limit) => limit?.codPaciente === codPaciente,
    ) ?? null;
  const disponivel = patientLimit?.disponivel ?? 0;
  const maxPerBlock = patientLimit?.limite ?? 0;

  const isCvRecurrence = isConsultorioVirtual(blockUnit.codUnidade);
  const quotaCap = isCvRecurrence ? maxPerBlock : disponivel;

  const anchor = useMemo(() => {
    if (!recurrence) return null;
    return getExtendAnchorDate({
      appointments: slots,
      diaDaSemana: recurrence.diaDaSemana,
      frequenciaEmDias: recurrence.frequenciaEmDias,
    });
  }, [recurrence, slots]);

  const [sessions, setSessions] = useState<RecurringSession[]>([]);
  const [slot, setSlot] = useState<{
    horaInicio: string;
    horaFim: string;
  } | null>(null);
  const [notificar, setNotificar] = useState(true);
  const [seeded, setSeeded] = useState(false);
  const [addFromDate, setAddFromDate] = useState<string | null>(null);

  const timesVariables = {
    codUnidade: blockUnit.codUnidade,
    codCompromisso: firstAppointment?.codUsuarioCompromisso ?? 0,
    codFormaRecebimento: firstAppointment?.codUsuarioFormaRecebimento ?? 0,
    horarioInicio: recurrence?.horaInicio ?? "",
    frequencia: recurrence?.frequenciaEmDias ?? 0,
    quantidadeSlots: maxPerBlock,
  };

  const hasTimesContext =
    Boolean(firstAppointment) && Boolean(recurrence) && maxPerBlock > 0;

  const seedResult = useGraphQL(
    RecurrenceExtendAvailabilityQuery,
    { ...timesVariables, dataInicio: anchor ?? "" },
    {
      enabled: hasTimesContext && quotaCap > 0 && Boolean(anchor) && !seeded,
      staleTime: 0,
      gcTime: 0,
    },
  );

  useEffect(() => {
    if (seeded || !recurrence) return;

    const blocos = seedResult.data?.buscaHorarioRecorrenteEspecifico?.blocos;
    if (!blocos) return;

    const bloco = blocos.at(0);
    if (bloco) {
      setSlot({ horaInicio: bloco.horaInicio, horaFim: bloco.horaFim });
      setSessions(seedSessionsCountingAvailable(bloco.datas, quotaCap));
    } else {
      setSlot({ horaInicio: recurrence.horaInicio, horaFim: recurrence.horaFim });
    }
    setSeeded(true);
  }, [seedResult.data, seeded, recurrence, quotaCap]);

  const nextResult = useGraphQL(
    RecurrenceExtendAvailabilityQuery,
    { ...timesVariables, dataInicio: addFromDate ?? "" },
    { enabled: addFromDate !== null && hasTimesContext, staleTime: 0, gcTime: 0 },
  );

  useEffect(() => {
    if (addFromDate === null) return;

    const blocos = nextResult.data?.buscaHorarioRecorrenteEspecifico?.blocos;
    if (!blocos) return;

    const bloco = blocos.at(0);
    const datesToAdd = bloco ? takeDatesUntilAvailable(bloco.datas, maxPerBlock) : [];

    if (datesToAdd.length > 0) {
      setSessions((current) =>
        datesToAdd.reduce((acc, date) => appendNextDate(acc, date), current),
      );
    }
    setAddFromDate(null);
  }, [nextResult.data, addFromDate, maxPerBlock]);

  const { mutateAsync, isPending } = useGraphQLMutationWithErrorHandler(
    EstenderRecorrenciaMutation,
    { retry: 1 },
  );
  const invalidateDetail = useInvalidateQuery(RecurringAppointmentDetailPageQuery, {
    codAgendamentoRecorrente: codRecorrencia,
  });
  const invalidateCalendarAppointments = useInvalidateQuery(
    CalendarPageAppointmentsQueryDocument,
  );
  const invalidateCalendarFreeSlots = useInvalidateQuery(
    CalendarPageFreeSlotsQueryDocument,
  );
  const invalidateWaitingRoom = useInvalidateQuery(WaitingRoomPageQueryDocument);

  const used = getUsedSessionsCount(sessions);
  const quotaUsed = getQuotaUsedSessionsCount(sessions, {
    baseUnitIsCv: isCvRecurrence,
  });
  const timeLabel = slot ? removeSecondsFromTimeString(slot.horaInicio) : "";
  const timeRangeLabel = slot
    ? `${timeLabel}-${removeSecondsFromTimeString(slot.horaFim)}`
    : undefined;
  const isAddingNextDate = addFromDate !== null && nextResult.isFetching;
  const addNextDateError =
    addFromDate !== null && nextResult.isError && !nextResult.isFetching;
  const isLoadingSchedules =
    !seeded &&
    !seedResult.isError &&
    (limitsResult.isLoading || (quotaCap > 0 && hasTimesContext));

  const handleAddNextDate = (): void => {
    if (addFromDate !== null) return;
    const nextStart =
      getNextRecurrenceDate(sessions, recurrence?.frequenciaEmDias ?? 7) ?? anchor;
    if (nextStart) setAddFromDate(nextStart);
  };

  const handleResolveSession = (
    sessionDate: string,
    resolution: RecurringSessionResolution,
  ): void => {
    trackEvent("Data Agendamento Recorrente Resolvida", {
      codUsuario: user.codUsuario,
      codAgendamentoRecorrente: codRecorrencia,
      frequency: recurrence?.frequenciaEmDias,
      data: sessionDate,
      resolution,
    });

    setSessions((current) => {
      if (resolution === "consultorioVirtual") {
        return resolveSessionAsConsultorioVirtual(
          current,
          sessionDate,
          COD_CONSULTORIO_VIRTUAL,
        );
      }

      return resolution === "kept"
        ? keepSession(current, sessionDate)
        : skipSession(current, sessionDate);
    });
  };

  const handleConfirm = async (): Promise<void> => {
    if (!slot) return;

    const input = buildEstenderRecorrenciaInput({
      codAgendamentoRecorrente: codRecorrencia,
      sessions,
      horaInicio: slot.horaInicio,
      notificar,
    });

    try {
      await mutateAsync(
        { input },
        {
          onSuccess: ({ estenderAgendamentoRecorrente }) => {
            const criadas = sessions
              .filter((session) => session.status === "active")
              .map((session) => session.data);

            trackEvent("Recorrência - Estender Confirmar", {
              codUsuario: user.codUsuario,
              codAgendamentoRecorrente: codRecorrencia,
              totalAgendamentos:
                estenderAgendamentoRecorrente.data?.codAgendamentos.length ?? 0,
            });

            invalidateDetail();
            invalidateCalendarAppointments();
            invalidateCalendarFreeSlots();
            invalidateWaitingRoom();

            void navigate({
              to: "/recurring-appointments/extend/success",
              search: {
                recurringAppointmentId: codRecorrencia,
                criadas: criadas.join(","),
              },
            });
          },
        },
      );
    } catch {}
  };

  const patientPhone =
    data?.agendamentos?.find((appointment) => appointment.Paciente?.telefone)?.Paciente
      ?.telefone ?? "";
  const unitName = blockUnit.nome;
  const unitAcronym = blockUnit.sigla;
  const pattern = recurrence
    ? formatRecurrenceScheduleLabel(recurrence, { includeFrequency: false })
    : "";
  const subtitle = recurrence
    ? [unitAcronym, getFrequencyLabel(recurrence.frequenciaEmDias)]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <RoundedLayout>
      <RecurrenceCreateHeader
        title="Adicionar agendamentos"
        subtitle={subtitle}
        onBack={() => router.history.back()}
      />
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {recurrence && firstAppointment ? (
            <div className="flex flex-col gap-4 pb-4">
              <RecurrenceReviewPatientCard
                name={recurrence.nomePaciente ?? ""}
                phone={patientPhone ? formatPhoneNumber(patientPhone) : ""}
                pattern={pattern}
              />

              <RecurrenceReviewInfoCard
                unitName={unitName}
                unitAcronym={unitAcronym}
                frequencyLabel={getFrequencyLabel(recurrence.frequenciaEmDias)}
                cap={quotaCap}
              />

              {isLoadingSchedules ? (
                <RecurrenceSessionsListSkeleton count={quotaCap} />
              ) : (
                <RecurrenceSessionsList
                  sessions={sessions}
                  used={used}
                  cap={maxPerBlock}
                  quotaUsed={quotaUsed}
                  quotaCap={quotaCap}
                  minimum={1}
                  timeLabel={timeLabel}
                  timeRangeLabel={timeRangeLabel}
                  unitName={unitName}
                  baseUnit={{
                    codUnidade: blockUnit.codUnidade,
                    label: unitAcronym || unitName,
                  }}
                  onResolveSession={handleResolveSession}
                  onAddNextDate={handleAddNextDate}
                  isAddingNextDate={isAddingNextDate}
                  addError={addNextDateError}
                  onRetryAddNextDate={() => void nextResult.refetch()}
                />
              )}

              <div className="flex flex-col gap-1 rounded-lg bg-neutral-50 p-4">
                <CpsCheckboxV2
                  className="bg-transparent gap-3 p-0"
                  value="notificar"
                  label="Notificar paciente por e-mail"
                  checked={notificar}
                  onCheckedChange={(checked) => setNotificar(checked === true)}
                />
                <p className="pl-9 text-neutral-500 text-xs">
                  Envia um e-mail avisando o paciente sobre os novos agendamentos.
                </p>
              </div>

              <Button
                fullWidth
                loading={isPending}
                disabled={
                  !canConfirmRecurrence({
                    used,
                    minimum: 1,
                    cap: maxPerBlock,
                    quotaUsed,
                    quotaCap,
                  })
                }
                onClick={() => void handleConfirm()}
              >
                Adicionar agendamentos
              </Button>
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute(
  "/recurring-appointments/extend/$recurringAppointmentId",
)({
  component: RecurringAppointmentExtendPage,
});
